<template>
    <div>
        <div class="grid grid-cols-12 mt-4 lg:mt-20 lg:gap-x-8">
            <div
                class="col-span-12 lg:col-span-8 h-full box-shadow-preview transition rounded-lg mb-8 sm:mb-8 lg:mb-0"
            >
                <div class="lg:sticky top-[var(--header-offset)]">
                    <div
                        class="bg-black bg-opacity-[3%] rounded-tl-lg rounded-tr-lg flex flex-col justify-center relative scale-110 lg:scale-100"
                    >
                        <div>
                            <div
                                id="preview-window"
                                ref="preview-window"
                                class="relative aspect-1-1 h-auto mx-auto w-auto transition max-h-[90vh] overflow-hidden"
                                :class="isHorizontal && '-rotate-90'"
                            >
                                <img
                                    class="absolute w-full h-full top-0 left-0 object-contain z-1 preview-shadow"
                                    :src="require('~/assets/images/editors/frames/shadow.webp')"
                                    :alt="$t('star_map.preview.shadow_alt')"
                                />
                                <img
                                    v-if="selectedFrame"
                                    :src="require(`~/assets/images/editors/frames/${selectedFrame}`)"
                                    class="absolute w-full h-full top-0 left-0 object-contain z-2 preview-frame"
                                    :alt="$t('star_map.fields.frame')"
                                    data-not-lazy
                                />
                                <img
                                    v-if="!starColors.custom"
                                    :src="require(`~/assets/images/editors/star-map/themes/${selectedTheme}`)"
                                    class="absolute w-full h-full top-0 left-0 object-contain z-3 preview-design"
                                    data-not-lazy
                                    :alt="$t('star_map.preview.theme_alt')"
                                />
                                <!-- same poster area as the theme artwork -->
                                <div
                                    v-else
                                    class="absolute z-3 left-[22.92%] top-[11.67%] w-[53.69%] h-[76.61%] transition"
                                    :style="{ backgroundColor: starColors.background }"
                                ></div>
                                <div
                                    class="absolute z-4 top-[17.25%] left-1/2 -translate-x-1/2 w-[38%] transition"
                                    :class="isHorizontal && 'rotate-90'"
                                >
                                    <client-only>
                                        <Map
                                            ref="star-map"
                                            :lat="selectedOptions.location.lat"
                                            :lng="selectedOptions.location.lng"
                                            :time="skyTime"
                                            :colors="{
                                                sky: starColors.custom ? starColors.sky : null,
                                                stars: starColors.stars,
                                                lines: starColors.lines,
                                            }"
                                            :show-constellations="selectedOptions.showConstellations"
                                            :show-grid="selectedOptions.showGrid"
                                            :show-border="selectedOptions.showCutoutFrame"
                                            :shape="skyShape"
                                            class="w-full h-auto"
                                        />
                                    </client-only>
                                </div>
                                <div
                                    ref="previewText"
                                    class="w-[52%] h-[27%] flex flex-col items-center justify-center absolute z-5 text-center top-[58%] left-1/2 -translate-x-1/2 transition"
                                    :class="isHorizontal && 'rotate-90 w-[33%]'"
                                    :style="{ color: starColors.text }"
                                >
                                    <span
                                        ref="previewTitle"
                                        class="block mb-[2%] preview-title leading-[0.75] max-w-full break-words"
                                        :style="titleStyle"
                                        :class="[
                                            selectedOptions.title?.typo?.class || selectedOptions.title?.typo || '',
                                            selectedOptions.title?.typo?.scale || '',
                                        ]"
                                        >{{ selectedOptions.title.value }}</span
                                    >
                                    <div
                                        ref="previewMessage"
                                        class="block mb-[7%] preview-message max-w-full break-words"
                                        :style="messageStyle"
                                        :class="setMessageSpacing"
                                        v-html="selectedOptions.message.value.replace(/\n/g, '<br>')"
                                    ></div>
                                    <span
                                        ref="previewDate"
                                        class="block mb-[2%] preview-date"
                                        :style="dateStyle"
                                        :class="[
                                            selectedOptions.date?.typo?.class || selectedOptions.date?.typo || '',
                                            selectedOptions.date?.typo?.scale || '',
                                        ]"
                                        >{{ $dayjs(selectedOptions.date.value).format('YYYY-MM-DD') }}</span
                                    >
                                </div>
                            </div>
                        </div>
                    </div>
                    <EditorHelp v-if="$device.isDesktop" class="hidden md:flex mb-8 lg:mb-0" />
                </div>
            </div>
            <div class="col-span-12 lg:col-span-4 relative">
                <div ref="scrollTargetTop"></div>
                <h2
                    class="font-Inter-Bold text-lg sm:text-xl mb-4 sm:mb-8 text-left border-b border-black border-dashed border-opacity-20 block pb-4"
                >
                    {{ $t('star_map.fields.customise') }}
                </h2>
                <div class="flex flex-col gap-y-4 sm:gap-y-6 relative">
                    <EditorFields v-slot="{ compact }">
                        <div class="location input-outer-wrapper">
                            <label
                                for="starMapLocation"
                                class="text-sm font-Inter-Bold lg:text-right text-grey-900 mb-2 lg:mb-3 inline-flex input-label"
                            >
                                {{ $t('star_map.fields.location') }}
                            </label>
                            <input
                                id="starMapLocation"
                                ref="locationInput"
                                v-model="selectedOptions.location.name"
                                type="text"
                                :placeholder="$t('star_map.fields.location_placeholder')"
                                class="p-inputtext p-component w-full"
                            />
                        </div>
                        <div class="date-text input-outer-wrapper">
                            <Input
                                v-model="dateTime"
                                class="w-full"
                                is-calendar
                                show-time
                                :placeholder="$t('star_map.fields.date_placeholder')"
                                :label="$t('star_map.fields.date_label')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <hr
                            v-if="$device.isDesktop"
                            class="border-b border-black border-dashed border-opacity-10 hidden lg:block"
                        />
                        <div class="preset input-outer-wrapper">
                            <span
                                class="text-sm font-Inter-Bold lg:text-right text-grey-900 mb-2 lg:mb-3 inline-flex input-label"
                            >
                                {{ $t('star_map.fields.presets') }}
                            </span>
                            <StylePresetSelect
                                :presets="stylePresets"
                                :value="activeStylePresetId"
                                :append-to="compact ? 'body' : null"
                                @select="applyStylePreset"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                        </div>
                        <div class="shape input-outer-wrapper">
                            <span
                                class="text-sm font-Inter-Bold lg:text-right text-grey-900 mb-2 lg:mb-3 inline-flex input-label"
                            >
                                {{ $t('star_map.fields.cutout') }}
                            </span>
                            <ShapeSelect
                                v-model="selectedOptions.shape.id"
                                :append-to="compact ? 'body' : null"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                            <div class="flex items-center justify-between mt-3">
                                <label for="showCutoutFrame" class="text-sm font-Inter-Semibold cursor-pointer">{{
                                    $t('star_map.fields.cutout_frame')
                                }}</label>
                                <InputSwitch
                                    v-model="selectedOptions.showCutoutFrame"
                                    input-id="showCutoutFrame"
                                />
                            </div>
                        </div>
                        <div
                            v-for="control in colorControls"
                            :key="control.key"
                            class="input-outer-wrapper"
                            :class="`${control.key}-color`"
                        >
                            <span
                                class="text-sm font-Inter-Bold lg:text-right text-grey-900 mb-2 lg:mb-3 inline-flex input-label"
                            >
                                {{ control.label }}
                            </span>
                            <ColorSwatches
                                :options="control.options"
                                :selected-id="selectedOptions[control.key].id"
                                :color="control.color"
                                :placeholder="control.placeholder"
                                @select="selectedOptions[control.key] = $event"
                                @show="onDropdownShow"
                                @hide="onDropdownHide"
                            />
                        </div>
                        <div class="sky-layers input-outer-wrapper flex flex-col">
                            <span class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label">
                                {{ $t('star_map.fields.display') }}
                            </span>
                            <div class="flex items-center justify-between">
                                <label for="showConstellations" class="text-sm font-Inter-Semibold cursor-pointer"
                                    >{{ $t('star_map.fields.constellations') }}</label
                                >
                                <InputSwitch
                                    v-model="selectedOptions.showConstellations"
                                    input-id="showConstellations"
                                />
                            </div>
                            <div class="flex items-center justify-between mt-3">
                                <label for="showGrid" class="text-sm font-Inter-Semibold cursor-pointer"
                                    >{{ $t('star_map.fields.grid') }}</label
                                >
                                <InputSwitch v-model="selectedOptions.showGrid" input-id="showGrid" />
                            </div>
                        </div>
                        <hr
                            v-if="$device.isDesktop"
                            class="border-b border-black border-dashed border-opacity-10 hidden lg:block"
                        />
                        <div class="title-text input-outer-wrapper">
                            <div class="editor-label-row flex justify-between items-center">
                                <span
                                    class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label"
                                >
                                    {{ $t('star_map.fields.title') }}
                                </span>
                                <TextSizeToggle
                                    :value="selectedOptions.title.size || 1"
                                    :label="$t('star_map.fields.title_size')"
                                    @input="setTextSize('title', $event)"
                                />
                            </div>
                            <Input
                                v-model="selectedOptions.title.value"
                                class="w-full"
                                :placeholder="$t('star_map.fields.title_placeholder')"
                                :required="false"
                                :maxlength="40"
                            />
                        </div>
                        <FontFamilySelector
                            :model-value="selectedOptions.title.typo"
                            :label="$t('star_map.fields.title_font')"
                            :show-tooltip="true"
                            wrapper-class="title-typo"
                            @update:modelValue="setTypo('title', $event)"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                        <hr
                            v-if="$device.isDesktop"
                            class="border-b border-black border-dashed border-opacity-10 hidden lg:block"
                        />
                        <div class="message-text input-outer-wrapper">
                            <div class="editor-label-row flex justify-between items-center">
                                <span
                                    class="text-sm font-Inter-Bold lg:text-right text-grey-900 inline-flex input-label"
                                >
                                    {{ $t('star_map.fields.message') }}
                                </span>
                                <TextSizeToggle
                                    :value="selectedOptions.message.size || DEFAULT_MESSAGE_SIZE"
                                    :label="$t('star_map.fields.message_size')"
                                    @input="setTextSize('message', $event)"
                                />
                            </div>
                            <Input
                                v-model="selectedOptions.message.value"
                                class="w-full"
                                :placeholder="$t('star_map.fields.message_placeholder')"
                                :required="false"
                                :maxlength="180"
                                :rows="3"
                                :is-textarea="$device.isDesktop"
                            />
                        </div>
                        <FontFamilySelector
                            :model-value="selectedOptions.message.typo"
                            :label="$t('star_map.fields.message_font')"
                            :show-tooltip="true"
                            wrapper-class="message-typo"
                            @update:modelValue="setTypo('message', $event)"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                        <FontFamilySelector
                            v-model="selectedOptions.date.typo"
                            :label="$t('star_map.fields.date_font')"
                            :show-tooltip="true"
                            wrapper-class="date-typo"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                        <hr
                            v-if="$device.isDesktop"
                            class="border-b border-black border-dashed border-opacity-10 hidden lg:block"
                        />
                        <div class="size input-outer-wrapper">
                            <Input
                                v-model="selectedOptions.size.id"
                                class="w-full"
                                :placeholder="$t('star_map.fields.size_placeholder')"
                                :options="sizeOptions"
                                is-dropdown
                                :label="$t('star_map.fields.size_label')"
                                :show="onDropdownShow"
                                :hide="onDropdownHide"
                            />
                        </div>
                        <CardPicker
                            v-model="selectedOptions.orientation"
                            name="orientation"
                            :label="$t('star_map.fields.orientation')"
                            :options="orientationOptions"
                            :src="(orientation) => require(`~/assets/images/dev/editor/${orientation.preview}`)"
                            :placeholder="$t('star_map.fields.orientation_placeholder')"
                            :append-to="compact ? 'body' : null"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                        <CardPicker
                            v-model="selectedOptions.frame.id"
                            name="frame"
                            :label="$t('star_map.fields.frame')"
                            :options="frameOptions"
                            :src="(frame) => require(`~/assets/images/dev/editor/${frame.icon}`)"
                            :placeholder="$t('star_map.fields.frame_placeholder')"
                            :clear-label="$t('star_map.fields.no_frame')"
                            :append-to="compact ? 'body' : null"
                            @clear="clearOption('frame')"
                            @show="onDropdownShow"
                            @hide="onDropdownHide"
                        />
                    </EditorFields>
                    <div class="w-full lg:mt-4 has-digitial-format z-10">
                        <Input
                            v-model="selectedOptions.hasDigital.active"
                            is-checkbox
                            :label="$t('star_map.fields.digital_label')"
                            :required="false"
                            class="font-Inter-Bold"
                        />
                    </div>
                    <div class="comment w-full z-10">
                        <Input
                            v-model="selectedOptions.comment"
                            is-textarea
                            :label="$t('star_map.fields.comment_label')"
                            :rows="3"
                            :placeholder="$t('star_map.fields.comment_placeholder')"
                            :required="false"
                        />
                    </div>
                    <EditorHelp v-if="$device.isMobile" mobile />
                    <StickyPriceCard
                        :quantity="quantity"
                        :name="$t('star_map.product_name')"
                        :price="price"
                        :image="previewImage"
                        :before-add-to-cart="capturePreview"
                        :slug="
                            localePath({
                                route: 'editors-star-map',
                                hash: `#${randomId}`,
                            })
                        "
                        :product-id="126"
                        :options="mutatedOptions"
                        :is-preview-capturing="isPreviewCapturing"
                        :is-dropdown-open="isDropdownOpen"
                        :scroll-target-bottom="$refs.scrollTargetBottom"
                        :scroll-target-top="$refs.scrollTargetTop"
                        :extras="cartExtras"
                    />
                    <div ref="scrollTargetBottom"></div>
                </div>
            </div>
            <div class="col-span-12 mt-8 lg:mt-32">
                <Gallery :images="gallery" folder="editor/" />
            </div>
            <div class="col-span-12 mt-8 mb-12 lg:mb-0 lg:mt-20">
                <h3
                    class="font-Inter-Bold pb-3 w-full block border-b border-dashed border-black border-opacity-20 text-xl mb-7"
                >
                    {{ $t('star_map.reviews') }}
                </h3>
                <div class="relative">
                    <div id="reviews-star-map-editor" class="swiper w-full lg:min-h-[10rem] lg:flex pt-2">
                        <div class="swiper-wrapper items-center">
                            <div
                                v-for="review in testimonials"
                                :key="`product-review-${review.id}`"
                                class="swiper-slide flex lg:flex-row flex-col justify-center lg:justify-start items-center px-4 lg:px-14"
                            >
                                <div class="flex flex-col" :class="review.image ? 'lg:w-8/12' : 'w-full'">
                                    <span class="block font-Inter-Semibold text-lg mb-3">{{ review.name }}</span>
                                    <Rating :is-product-card="false" :rating-value="5" class="mb-4 italic" />
                                    <p class="block font-Inter-Regular text-base text-left lg:pr-12 leading-[1.7]">
                                        {{ review.text }}
                                    </p>
                                </div>
                                <div v-if="review.image" class="lg:w-4/12 mt-4 lg:mt-0 relative group">
                                    <div
                                        class="aspect-w-4 aspect-h-3 rounded-lg overflow-hidden border border-black border-opacity-10"
                                    >
                                        <app-image :src="review.image" :alt="review.name" preset="photo" />
                                    </div>
                                    <button
                                        class="zoom-button rounded-full w-8 h-8 bg-white bg-no-repeat bg-center absolute right-4 top-4 cursor-pointer z-10 transition opacity-0 group-hover:opacity-100"
                                        @click="openLightbox(review.image)"
                                    >
                                        <i class="ri-zoom-in-line"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div class="swiper-button-prev -left-5 lg:-left-2"></div>
                    <div class="swiper-button-next -right-5 lg:-right-2"></div>
                    <div class="mt-10 lg:mt-16 relative hidden lg:flex">
                        <div class="swiper-pagination"></div>
                    </div>
                </div>
                <BaseModal :close="closeLightbox" :is-open="isLightboxOpen">
                    <div class="bg-white rounded-md w-screen lg:w-[60vw] h-[50vh] max-h-[95vh] lg:h-auto p-8">
                        <img
                            v-show="currentImage"
                            :src="currentImage"
                            :alt="currentImage"
                            class="mx-auto max-h-[80vh] w-full"
                        />
                    </div>
                </BaseModal>
            </div>
        </div>
    </div>
</template>

<script>
import Map from '@/components/Editor/StarMap/Map.vue';
import CardPicker from '@/components/Editor/Shared/CardPicker.vue';
import ColorSwatches from '@/components/Editor/Shared/ColorSwatches.vue';
import EditorFields from '@/components/Editor/Shared/EditorFields.vue';
import EditorHelp from '@/components/Editor/Shared/EditorHelp.vue';
import ShapeSelect from '@/components/Editor/Shared/ShapeSelect.vue';
import StylePresetSelect from '@/components/Editor/Shared/StylePresetSelect.vue';
import TextSizeToggle from '@/components/Editor/Shared/TextSizeToggle.vue';
import editorMixin from '@/components/Editor/Shared/editorMixin';
import skyPlaceMixin from '@/components/Editor/StarMap/skyPlaceMixin';
import {
    DEFAULT_MESSAGE_SIZE,
    DEFAULT_MESSAGE_TYPO,
    DEFAULT_TITLE_TYPO,
    ORIENTATIONS,
} from '@/components/Editor/Shared/productOptions';
import { textOverflows, FIT_STEP, MIN_FIT } from '@/components/Editor/Shared/textFit';
import { SHAPE_CUTOUTS, shapeById } from '@/components/Editor/Shared/shapes';
import { TEXT_COLORS } from '@/components/Editor/Shared/designTokens';
import { STAR_MAP_THEMES } from '@/components/Editor/StarMap/options';
import {
    STAR_MAP_BACKGROUNDS,
    buildStarMapPresets,
    resolveStarMapColors,
    starMapPresetMatches,
} from '@/components/Editor/StarMap/stylePresets';
import Gallery from '@/components/Sections/Gallery.vue';
import Rating from '@/components/UI/Rating.vue';
import BaseModal from '@/components/UI/BaseModal.vue';
import StickyPriceCard from '@/components/Cards/StickyPriceCard/StickyPriceCard.vue';
import FontFamilySelector from '@/components/UI/FontFamilySelector/FontFamilySelector.vue';

const SCRIPT_FONTS = ['Mrs Saint Delafield', 'Sacramento Regular', 'Narum Myeongjo Regular', 'NrDafoe Pro'];
const STAR_MAP_STYLE_PRESETS = buildStarMapPresets(STAR_MAP_THEMES);
const MIDNIGHT_LOOK = STAR_MAP_STYLE_PRESETS.find((preset) => preset.labelKey === 'star_map.preset.midnight_blue')?.options;
const MIDNIGHT_THEME = STAR_MAP_THEMES.find((theme) => theme.id === MIDNIGHT_LOOK?.theme) || STAR_MAP_THEMES[0];

export default {
    name: 'StarMapEditor',
    components: {
        Map,
        Gallery,
        Rating,
        BaseModal,
        StickyPriceCard,
        FontFamilySelector,
        CardPicker,
        ColorSwatches,
        EditorFields,
        EditorHelp,
        ShapeSelect,
        StylePresetSelect,
        TextSizeToggle,
    },
    mixins: [editorMixin, skyPlaceMixin],
    props: {
        testimonials: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            DEFAULT_MESSAGE_SIZE,
            basePrice: 11000,
            editorType: 'star-map',
            orientationOptions: ORIENTATIONS,
            gallery: [
                { title: 'Title 1', src: 'gallery_image-5.jpg' },
                { title: 'Title 2', src: 'gallery_image-2.jpg' },
                { title: 'Title 3', src: 'gallery_image-3.jpg' },
                { title: 'Title 4', src: 'gallery_image-4.jpg' },
                { title: 'Title 5', src: 'gallery_image-1.jpg' },
            ],
            isHorizontal: false,
            titleStyle: {},
            messageStyle: {},
            dateStyle: {},
            selectedOptions: {
                size: { id: 1, name: 'A5', price: 0 },
                frame: { id: 2, name: 'Fehér', preview: 'frame_2.png', price: 2000 },
                theme: { ...MIDNIGHT_THEME },
                orientation: 1,
                title: {
                    value: 'I Love You',
                    size: 1,
                    typo: { ...(MIDNIGHT_LOOK?.titleTypo || DEFAULT_TITLE_TYPO) },
                    price: 0,
                },
                message: {
                    value: this.$t('star_map.default_message'),
                    size: DEFAULT_MESSAGE_SIZE,
                    typo: { ...(MIDNIGHT_LOOK?.messageTypo || DEFAULT_MESSAGE_TYPO) },
                    price: 0,
                },
                date: { value: this.$dayjs().format('YYYY-MM-DD'), typo: { ...DEFAULT_TITLE_TYPO }, price: 0 },
                // Local time at the location; utcOffset (minutes) null means the browser's time zone.
                time: '22:00',
                location: { name: 'Budapest', lat: 47.4979, lng: 19.0402, utcOffset: null },
                background: { ...(MIDNIGHT_LOOK?.background || STAR_MAP_BACKGROUNDS[0]) },
                sky: { ...(MIDNIGHT_LOOK?.sky || STAR_MAP_BACKGROUNDS[0]) },
                starColor: { ...(MIDNIGHT_LOOK?.starColor || TEXT_COLORS[0]) },
                lineColor: { ...(MIDNIGHT_LOOK?.lineColor || TEXT_COLORS[0]) },
                textColor: { ...(MIDNIGHT_LOOK?.textColor || TEXT_COLORS[0]) },
                showConstellations: true,
                showGrid: true,
                showCutoutFrame: true,
                shape: { id: MIDNIGHT_LOOK?.shape?.id || 1 },
                comment: null,
                preview: null,
                hasDigital: { active: false, price: 500 },
            },
            currentImage: null,
            isLightboxOpen: false,
        };
    },
    computed: {
        setMessageSpacing() {
            const { typo } = this.selectedOptions.message;
            if (!typo) return ['leading-[1.1]'];
            return [
                typo.class || typo || '',
                typo.scale || '',
                SCRIPT_FONTS.includes(typo.label) ? 'mb-0 mt-4 leading-[0.85] w-[80%]' : 'leading-[1.1]',
            ];
        },
        selectedThemeOption() {
            return STAR_MAP_THEMES.find((theme) => theme.id === this.selectedOptions.theme.id);
        },
        selectedTheme() {
            return this.selectedThemeOption?.preview || this.selectedOptions.theme.preview;
        },
        starColors() {
            const { background, sky, starColor, lineColor, textColor } = this.selectedOptions;
            return resolveStarMapColors(this.selectedThemeOption || STAR_MAP_THEMES[0], {
                shaped: Boolean(this.skyShape),
                background,
                sky,
                stars: starColor,
                lines: lineColor,
                text: textColor,
            });
        },
        skyShape() {
            const shape = shapeById(this.selectedOptions.shape?.id ?? 1);
            return SHAPE_CUTOUTS[shape.preview];
        },
        colorControls() {
            const colors = this.starColors;
            return [
                { key: 'background', label: this.$t('star_map.colors.background'), options: STAR_MAP_BACKGROUNDS, color: colors.background },
                { key: 'sky', label: this.$t('star_map.colors.sky'), options: STAR_MAP_BACKGROUNDS, color: colors.sky },
                { key: 'starColor', label: this.$t('star_map.colors.stars'), options: TEXT_COLORS, color: colors.stars },
                { key: 'lineColor', label: this.$t('star_map.colors.lines'), options: TEXT_COLORS, color: colors.lines },
                { key: 'textColor', label: this.$t('star_map.colors.text'), options: TEXT_COLORS, color: colors.text },
            ].map((control) => ({ ...control, placeholder: this.$t('star_map.colors.placeholder') }));
        },
        stylePresets() {
            return STAR_MAP_STYLE_PRESETS;
        },
        activeStylePresetId() {
            const active = this.stylePresets.find((preset) => starMapPresetMatches(preset, this.selectedOptions));
            return active ? active.id : null;
        },
    },
    watch: {
        selectedOptions: {
            handler() {
                this.debouncedCheckOptions(this.selectedOptions);
                this.updateFontSize();
            },
            deep: true,
        },
        'selectedOptions.orientation'(value) {
            this.isHorizontal = value === 2;
        },
    },
    mounted() {
        this.reviewCarousel = new this.$swiper(`#reviews-star-map-editor`, {
            slidesPerView: 1,
            spaceBetween: 10,
            loop: true,
            slideToClickedSlide: true,
            pagination: {
                el: '.swiper-pagination',
                type: 'bullets',
                clickable: true,
            },
            navigation: {
                nextEl: '.swiper-button-next',
                prevEl: '.swiper-button-prev',
            },
        });
        this.$primevue.config.locale = this.$primevue.config.locales[this.$i18n.locale];
        this.updateFontSize();

        const saved = this.loadSavedProduct();
        if (saved) {
            // Merge so products saved before the sky/colour options existed still get their defaults.
            this.selectedOptions = { ...this.selectedOptions, ...this.$cloneDeep(saved) };
        }
        this.initPlaces();
        window.addEventListener('resize', this.updateFontSize);
        this.checkOptions(this.selectedOptions);
    },
    beforeDestroy() {
        this.reviewCarousel.destroy();
        window.removeEventListener('resize', this.updateFontSize);
    },
    methods: {
        applyStylePreset(preset) {
            if (!preset) return;
            // the rest are the colour options and the shape, stored on selectedOptions as they are
            const { theme, titleTypo, messageTypo, ...rest } = preset.options;
            this.selectedOptions = JSON.parse(
                JSON.stringify({
                    ...this.selectedOptions,
                    ...rest,
                    theme: { ...this.selectedOptions.theme, id: theme },
                    title: { ...this.selectedOptions.title, typo: titleTypo || this.selectedOptions.title.typo },
                    message: {
                        ...this.selectedOptions.message,
                        typo: messageTypo || this.selectedOptions.message.typo,
                    },
                })
            );
        },
        updateFontSize() {
            this.$nextTick(() => {
                this.fitText();
                // web fonts change the text metrics once they finish loading
                if (document.fonts) document.fonts.ready.then(this.fitText);
            });
        },
        // Sizes the texts from the preview width and the chosen size, then shrinks them until they fit the text box.
        fitText() {
            const previewWindow = this.$refs['preview-window'];
            const box = this.$refs.previewText;
            if (!previewWindow || !previewWindow.clientWidth || !box) {
                return;
            }

            const containerWidth = previewWindow.clientWidth;
            const elements = {
                title: this.$refs.previewTitle,
                message: this.$refs.previewMessage,
                date: this.$refs.previewDate,
            };
            const baseSizes = {
                title: (containerWidth / 17) * (this.selectedOptions.title.size || 1),
                message:
                    (containerWidth / (this.$device.isMobile ? 30 : 36)) *
                    (this.selectedOptions.message.size || DEFAULT_MESSAGE_SIZE),
                date: containerWidth / 55,
            };
            const keys = Object.keys(elements).filter((key) => elements[key]);
            const apply = (fit) =>
                keys.forEach((key) => {
                    elements[key].style.fontSize = `${baseSizes[key] * fit}px`;
                });

            let fit = 1;
            apply(fit);
            while (fit > MIN_FIT && textOverflows(box, Object.values(elements))) {
                fit -= FIT_STEP;
                apply(fit);
            }

            this.titleStyle = { fontSize: `${baseSizes.title * fit}px` };
            this.messageStyle = { fontSize: `${baseSizes.message * fit}px`, marginTop: '3%' };
            this.dateStyle = { fontSize: `${baseSizes.date * fit}px` };
        },
        openLightbox(image) {
            this.currentImage = image;
            this.isLightboxOpen = true;
        },
        closeLightbox() {
            this.currentImage = null;
            this.isLightboxOpen = false;
        },
    },
};
</script>

<style lang="postcss">
.font-dropdown {
    .p-disabled {
        @apply opacity-100;
        &:nth-child(2) {
            margin-top: 0.5rem !important;
        }
    }
    .p-dropdown-item {
        @apply mb-1;
    }
}

.quantity input {
    @apply text-center;
}

.box-shadow-preview {
    box-shadow: none !important;
    @media (min-width: 992px) {
        box-shadow: 0 4px 60px 0 rgba(21, 33, 55, 0.08);
    }
}

.preview-title {
    font-size: 3em;
}
.preview-message {
    font-size: 1.15em;
    white-space: pre-wrap;
}
.preview-date {
    font-size: 1em;
}

.transition--slow {
    transition-duration: 2s;
}

.loader {
    width: 23px;
    aspect-ratio: 1;
    border-radius: 50%;
    border: 3px solid #001426;
    animation: l20-1 0.8s infinite linear alternate, l20-2 1.6s infinite linear;
}
@keyframes l20-1 {
    0% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 50% 0, 50% 0, 50% 0%, 50% 0%);
    }
    12.5% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 0, 100% 0%, 100% 0%);
    }
    25% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 100%, 100% 100%, 100% 100%);
    }
    50% {
        clip-path: polygon(50% 50%, 0 0, 50% 0, 100% 0, 100% 100%, 50% 100%, 0% 100%);
    }
    62.5% {
        clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%);
    }
    75% {
        clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%);
    }
    100% {
        clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%);
    }
}
@keyframes l20-2 {
    0% {
        transform: scaleY(1) rotate(0deg);
    }
    49.99% {
        transform: scaleY(1) rotate(135deg);
    }
    50% {
        transform: scaleY(-1) rotate(0deg);
    }
    100% {
        transform: scaleY(-1) rotate(-135deg);
    }
}
</style>
